import { differenceInSeconds } from '@superbet-group/web.lib.utilities';
import {
    forEach as _forEach,
    isEmpty as _isEmpty,
    map as _map,
    partition as _partition,
    keyBy,
} from 'lodash';
import {
    ActionContext,
    Commit,
    Dispatch,
} from 'vuex';

import { IState } from '@app/store';
import OfferEvent from '@core/models/offer/OfferEvent';
import OfferService from '@core/services/offer/OfferService';
import { StructManager } from '@core/services/offer/StructManager';
import StructService from '@core/services/offer/StructService';
import Odd from '@models/offer/Odd';
import Category from '@models/struct/Category';
import Market from '@models/struct/Market';
import MarketGroupInfo from '@models/struct/MarketGroupInfo';
import OddType from '@models/struct/OddType';
import Sport from '@models/struct/Sport';
import Tournament from '@models/struct/Tournament';
import { getTimeSpanForTimeFilter } from '@shared/timeFilter/helpers';
import {
    TimeFilter,
    TimeSpan,
} from '@shared/timeFilter/types';
import StoreUtil from '@store/StoreUtil';
import { OddValueChange } from '@store/modules/data/sportOffer/enums';
import {
    asyncErrorGuard,
    errorGuard,
} from '@utils/ErrorGuard';
import BookieDate from '@utils/date/BookieDate';

import {
    dispatchOfferStructUpdates,
    findExpiredEventIds,
    getStartAndEndDate,
} from './helpers';
import { ISportOfferState } from './index';
import * as types from './mutationTypes';
import {
    ISuperOfferCollections,
    OddDynamics,
} from './types';
import {
    OutOfSyncUpdate,
    SseStructure,
    SseUpdate,
} from '@core/services/offer/_private/sse/types';
import { ISportOfferGetters } from '@store/modules/ui/sportOffer/sportPage/types';
import { MarketGroupPhase } from '@core/services/offer/_private/helpers/types';
import {
    getPopularSuperbets,
    getPopularSuperbetsV2,
} from '@core/services/offer/CmsOfferService';
import {
    isInCache,
    markAsCached,
    purgeEventsByDateCache,
} from './eventCache';
import { OfferRequestParams } from '@core/services/offer/_private/models/OfferRequestParams';

const pendingEvents: Record<string, OfferEvent> = {};

export default {
    fetchStruct: StoreUtil.asyncErrorGuard(
        async (
            { commit }: { commit: Commit },
        ) => {
            const struct = await StructService.getInstance().getStruct();
            let structManager;
            structManager = StructManager.getInstance();
            structManager.setStruct(struct);

            commit(types.SET_STRUCT, structManager.getStruct());
        }),

    startOffer: asyncErrorGuard<ISportOfferState, IState, void, void>(async (context: ActionContext<ISportOfferState, IState>) => {
        context.dispatch('setIsOfferStarting', true);

        context.dispatch('fetchSportsWithEvents');

        await context.dispatch('fetchStruct');

        context.dispatch('data/tvChannels/fetchTvChannels', null, { root: true });
        context.dispatch('setIsOfferReady', true);
        context.dispatch('setIsOfferStarting', false);
        context.dispatch('startLiveEventsCounting');
        context.dispatch('data/competitions/fetchHotTournaments', null, { root: true });
    }),

    subscribeToStructChanges: StoreUtil.asyncErrorGuard((
        { dispatch, getters, commit }: { dispatch: Dispatch, getters: ISportOfferGetters, commit: Commit },
    ) => {
        OfferService.getInstance().subscribeToStructChanges((struct: SseUpdate<Partial<SseStructure>>) => {
            if (struct === OutOfSyncUpdate) {
                if (getters.isFirstStructSync) {
                    commit(types.SET_IS_FIRST_STRUCT_SYNC, false);
                } else {
                    dispatch('fetchStruct');
                }
            } else {
                dispatchOfferStructUpdates(struct, dispatch);
            }
        });
    }),

    fetchEvents: StoreUtil.asyncErrorGuard(async (
        { getters, dispatch, commit, rootGetters }: { getters: any; dispatch: Dispatch; commit: Commit; rootGetters: any },
        { timeSpan, sportId }: { timeSpan?: TimeSpan, sportId?: number } = {},
    ) => {
        const { startDate, endDate } = getStartAndEndDate(timeSpan);
        const timeFilter: TimeFilter = rootGetters['navigation/timeFilter'];

        if (isInCache(timeSpan, timeFilter, sportId)) {
            return;
        }

        const shouldLoadLiveOffer = timeFilter == null
            || rootGetters['navigation/isOnHomePage']
            || timeFilter === TimeFilter.Live
            || timeFilter === TimeFilter.All; // Note: needed, at least temporarily, for competition page

        const [prematchEvents, live] = await Promise.all([
            OfferService.getInstance().getEvents(startDate, endDate, sportId),
            shouldLoadLiveOffer ? OfferService.getInstance().getLiveEvents() : [] as OfferEvent[],
        ]);

        // purge only when looking at live, otherwise live array will be empty due to timeFilter
        if (live.length > 0 || timeFilter === TimeFilter.Live) {
            const storedLiveEventIds = getters.events.filter((event: OfferEvent) => event.isOngoing()).map((e: OfferEvent) => e.id);
            const expiredEventIds = findExpiredEventIds(storedLiveEventIds, live.map((e) => e.id));
            commit(types.REMOVE_EVENTS, expiredEventIds);
        }

        const prematchEventsToAdd: OfferEvent[] = [];
        prematchEvents.forEach((event) => {
            if (!getters.eventsMap[event.id]) {
                prematchEventsToAdd.push(event);
            } else if (event.incrementId >= getters.eventsMap[event.id].incrementId) {
                commit(types.UPDATE_EVENT, event);
            }
        });

        dispatch(
            'setEvents',
            {
                events: [
                    ...prematchEventsToAdd,
                    ...live.filter((event) => !getters.eventsMap[event.id]
                        || getters.eventsMap[event.id].incrementId < event.incrementId),
                ],
            },
        );

        if (sportId == null) {
            commit(types.SET_IS_OFFER_FETCHED, true);
        } else {
            commit(types.SET_IS_OFFER_FETCHED_BY_SPORT_ID, sportId);
        }

        commit(types.SET_ARE_LIVE_EVENTS_FETCHED, shouldLoadLiveOffer || live.length > 0);
        markAsCached(timeSpan, timeFilter, sportId);
    }),

    fetchEventsForTournaments: asyncErrorGuard(async (context: ActionContext<ISportOfferState, IState>, tournamentIds: number[]): Promise<void> => {
        if (tournamentIds.length === 0) {
            return;
        }

        // TODO: Consider a cache here
        const [prematchEvents, live] = await Promise.all([
            OfferService.getInstance().getPrematchEventsForTournaments(tournamentIds),
            OfferService.getInstance().getLiveEventsForTournaments(tournamentIds),
        ]);

        if (live.length > 0) {
            const storedLiveEventIds = context.getters.events.filter((event: OfferEvent) => event.isOngoing()).map((e: OfferEvent) => e.id);
            const expiredEventIds = findExpiredEventIds(storedLiveEventIds, live.map((e) => e.id));
            context.commit(types.REMOVE_EVENTS, expiredEventIds);
        }

        const prematchEventsToAdd: OfferEvent[] = [];
        prematchEvents.forEach((event) => {
            if (!context.getters.eventsMap[event.id]) {
                prematchEventsToAdd.push(event);
            } else if (event.incrementId >= context.getters.eventsMap[event.id].incrementId) {
                context.commit(types.UPDATE_EVENT, event);
            }
        });

        context.dispatch(
            'setEvents',
            {
                events: [
                    ...prematchEventsToAdd,
                    ...live.filter((event) => !context.getters.eventsMap[event.id]
                        || context.getters.eventsMap[event.id].incrementId < event.incrementId),
                ],
            },
        );
    }),

    fetchSuperAdvantageEvents: asyncErrorGuard(async (
        { dispatch }: { dispatch: Dispatch },
    ) => {
        const { startDate, endDate } = BookieDate.getSuperAdvantageDateRange();

        const superAdvantageEvents = await OfferService.getInstance().getSuperAdvantageEvents(startDate, endDate);
        dispatch(
            'setEvents',
            {
                events: superAdvantageEvents,
            },
        );
    }),

    fetchPriceBoostEvents: asyncErrorGuard(async (
        { dispatch }: { dispatch: Dispatch },
    ) => {
        const { startDate, endDate } = BookieDate.getPriceBoostDateRange();

        const priceBoostEvents = await OfferService.getInstance().getPriceBoostEvents(startDate, endDate);
        dispatch(
            'setEvents',
            {
                events: priceBoostEvents,
            },
        );
    }),

    fetchLiveEvents: StoreUtil.asyncErrorGuard(async (
        { dispatch, commit }: { dispatch: Dispatch, commit: Commit },
    ) => {
        const liveEvents = await OfferService.getInstance().getLiveEvents();
        dispatch(
            'setEvents',
            {
                events: liveEvents,
            },
        );
        commit(types.SET_ARE_LIVE_EVENTS_FETCHED, true);
    }),

    fetchAll: StoreUtil.asyncErrorGuard(async (
        { dispatch }: { dispatch: Dispatch },
    ) => {
        const timeSpan = getTimeSpanForTimeFilter(TimeFilter.All);
        await dispatch('fetchEvents', { timeSpan });
    }),

    fetchTopEvents: StoreUtil.asyncErrorGuard(async (
        { commit }: { commit: Commit; dispatch: Dispatch }) => {
        const topEventIds = await OfferService.getInstance().getTopEventIds();
        commit(types.SET_TOP_EVENT_IDS, topEventIds);
        commit(types.SET_ARE_TOP_EVENT_IDS_FETCHED, true);
    }),

    fetchOutrights: asyncErrorGuard<ISportOfferState, IState, void, void>(async (
        { dispatch }: ActionContext<ISportOfferState, IState>) => {
        const events = await OfferService.getInstance().getOutrights();
        dispatch(
            'setEvents',
            {
                events,
            },
        );
    }),

    fetchSuperEvents: StoreUtil.asyncErrorGuard(
        // eslint-disable-next-line sonarjs/cognitive-complexity
        async (context: ActionContext<ISportOfferState, IState>) => {
            if (context.getters.isSuperOfferFetched) {
                return;
            }

            const { topTen, ...superOffer } = await OfferService.getInstance().getSuperEvents(
                BookieDate.getStartDateForDate(),
                BookieDate.getEndDateForDate(),
            );

            const promotionEvents = {} as ISuperOfferCollections;
            const events = [...topTen];
            _forEach(superOffer, (promoEvents, key) => {
                if (promoEvents.length > 0) {
                    let promoMarketOdd: ReturnType<OfferEvent['getOdds']>[0] | undefined;
                    for (let i = 0; i < promoEvents.length; i += 1) {
                        promoMarketOdd = promoEvents[i].getOdds().find((odd) =>
                            (odd.tags?.includes(key)));
                        if (promoMarketOdd) {
                            break;
                        }
                    }
                    if (!promoMarketOdd) {
                        delete promotionEvents[key];
                        return;
                    }
                    promotionEvents[key] = {
                        matchIds: promoEvents.map((m) => m.id),
                        primaryMarketId: promoMarketOdd!.marketId,
                    };
                    events.push(...promoEvents);
                }
            });
            context.commit(types.SET_TOP_TEN_EVENTS, _map(topTen, 'id'));
            context.dispatch(
                'setEvents',
                {
                    events,
                },
            );
            context.commit(types.SET_PROMOTIONAL_EVENTS, promotionEvents);
            context.commit(types.SET_IS_SUPER_OFFER_FETCHED, true);
            context.commit(types.SET_ARE_TOP_TEN_EVENTS_FETCHED, true);
        }),

    // eslint-disable-next-line sonarjs/cognitive-complexity
    subscribeToEventChanges(context: ActionContext<ISportOfferState, unknown>): void {
        if (context.getters.isEventsSubscriptionSet) {
            return;
        }

        OfferService.getInstance().subscribeToAllEventChanges((events) => {
            if (events === OutOfSyncUpdate) {
                if (context.getters.isFirstDailyOfferSync) {
                    context.commit(types.SET_IS_FIRST_DAILY_OFFER_SYNC, false);
                } else {
                    purgeEventsByDateCache();
                    const timeFilter = context.rootGetters['navigation/timeFilter'];
                    const timeSpan = getTimeSpanForTimeFilter(timeFilter);
                    context.dispatch('fetchEvents', { timeSpan });
                    context.dispatch('fetchSportsWithEvents');
                }
            } else {
                errorGuard(async () => {
                    context.dispatch('_purgeOddValueDynamics');

                    if (_isEmpty(events)) {
                        return;
                    }

                    await context.dispatch('_setOddValueDynamics', events);
                    await context.dispatch(
                        'setEvents',
                        {
                            events,
                        },
                    );
                    events.forEach((event) => {
                        if (!event.isOngoing() && !context.state.sportsWithEvents.includes(event.sportId)) {
                            context.commit(types.SET_SPORTS_WITH_EVENTS, [...context.state.sportsWithEvents, event.sportId]);
                        }
                    });
                });
            }
        });

        context.dispatch('setIsEventsSubscriptionSet', true);
    },

    subscribeToEvent: StoreUtil.asyncErrorGuard((
        { dispatch }: { dispatch: Dispatch },
        eventId: number,
    ) => {
        OfferService.getInstance().subscribeToEvent(eventId, async (event: SseUpdate<OfferEvent>) => {
            if (event === OutOfSyncUpdate) {
                dispatch('fetchEventsByIds', { eventIds: [eventId] });
            } else {
                dispatch('_purgeOddValueDynamics');
                await dispatch('_setOddValueDynamics', [event]);
                await dispatch(
                    'setEvents',
                    {
                        events: [event],
                    },
                );
            }
        });
    }),

    setEvents(
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        { events }: { events: OfferEvent[] },
    ): void {
        const [valid, invalid] = _partition(events, (e) => e.validStruct);

        commit(types.SET_EVENTS, valid);
        commit(types.INCREMENT_ALL_CHANGES_ID);

        if (invalid.length > 0) {
            dispatch('retryInvalidEvents', invalid);
        }
    },

    retryInvalidEvents: StoreUtil.asyncErrorGuard(async (
        { dispatch }: { dispatch: Dispatch },
        pending: OfferEvent[],
    ) => {
        pending.forEach((event) => pendingEvents[event.id] = event);
        await dispatch('fetchStruct');
        dispatch('denormalizePendingEvents');
    }),

    denormalizePendingEvents({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }): Promise<(OfferEvent | null)[]> {
        return Promise.all(Object.keys(pendingEvents).map(async (id) => {
            const event = pendingEvents[id];
            await OfferService.getInstance().denormalizeEvent(event);
            if (event.validStruct) {
                delete pendingEvents[id];
                dispatch('_setOddValueDynamics', [event]);
                commit(types.SET_EVENT, event);
                return event;
            }
            return null;
        }));
    },

    removeSubscription(_context: unknown, eventId: number): void {
        OfferService.getInstance().unsubscribeFromEvent(eventId);
    },

    getEvent({ dispatch }: { dispatch: Dispatch }, eventId: number): Promise<void> {
        return dispatch('getEvents', [eventId]);
    },

    async getEventByBetRadarId({ dispatch }: { dispatch: Dispatch; }, id: number): Promise<void> {
        const event = await OfferService.getInstance().getEventByBetRadarId(id);

        dispatch(
            'setEvents',
            { events: [event] },
        );
    },

    getEvents: StoreUtil.asyncErrorGuard(async (
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        eventIds: number[],
    ) => {
        eventIds.forEach((eventId) => {
            // because pick is a new component and on expanding and it shows
            // odd changes because oddValueDynamics still contains the last value for a short period of time
            dispatch('_deleteFromOddValueDynamics', eventId);
        });
        const events = await OfferService.getInstance().getEventsById(eventIds);
        eventIds.forEach((eventId) => dispatch('subscribeToEvent', eventId));

        commit(types.SET_EVENTS, events);
    }),

    fetchEventsByIds: StoreUtil.asyncErrorGuard(async (
        { getters, dispatch }: { getters: any; dispatch: Dispatch },
        { eventIds, params }: { eventIds: number[], params?: OfferRequestParams },
    ) => {
        const events = await OfferService.getInstance().getEventsById(eventIds, params);
        await dispatch(
            'setEvents',
            {
                events: events.filter((event) => !getters.eventsMap[event.id]
                    || getters.eventsMap[event.id].incrementId !== event.incrementId),
            },
        );
    }),

    fetchAndMergeEventsByIds: StoreUtil.asyncErrorGuard(async (
        { dispatch }: { getters: any; dispatch: Dispatch },
        { eventIds, params }: { eventIds: number[], params?: OfferRequestParams },
    ) => {
        const events = await OfferService.getInstance().getEventsById(eventIds, params);
        await dispatch(
            'setEvents',
            {
                events,
            },
        );
    }),

    fetchCompetitionTreeBySportId: asyncErrorGuard(async (context: ActionContext<ISportOfferState, unknown>, payload: { sportId: number, force?: boolean }) => {
        if (!payload.force && context.state.isCompetitionTreeFetchBySportId[payload.sportId]) {
            return;
        }

        context.commit(types.SET_IS_COMPETITION_TREE_FETCHED_FOR_SPORT_ID, { sportId: payload.sportId, isFetched: false });

        const competitionTree = await OfferService.getInstance().getCategoryWithCompetitionsBySportId(payload.sportId);

        context.commit(types.SET_COMPETITION_TREE_FOR_SPORT_ID, { sportId: payload.sportId, competitionTree });
        context.commit(types.SET_IS_COMPETITION_TREE_FETCHED_FOR_SPORT_ID, { sportId: payload.sportId, isFetched: true });
    }),
    setIsOfferStarting: StoreUtil.createSimpleMutatorAction(types.SET_IS_OFFER_STARTING),
    setIsOfferReady: StoreUtil.createSimpleMutatorAction(types.SET_IS_OFFER_READY),
    setEvent: StoreUtil.createSimpleMutatorAction(types.SET_EVENT),
    setAreSubscriptionsSet: (context: ActionContext<ISportOfferState, unknown>, areSubsSet: boolean) => {
        context.commit(types.SET_ARE_SUBSCRIPTIONS_SET, areSubsSet);
        if (areSubsSet) {
            // The latter is a special-case subset of the former when only minimal data is needed
            // (e.g., on tickets)
            context.commit(types.SET_IS_EVENTS_SUBSCRIPTION_SET, true);
        }
    },
    setIsEventsSubscriptionSet: StoreUtil.createSimpleMutatorAction(types.SET_IS_EVENTS_SUBSCRIPTION_SET),
    updateSports: StoreUtil.asyncErrorGuard(({ commit, dispatch, state }: { commit: any; dispatch: Dispatch; state: ISportOfferState }, sport: Sport) => {
        if (sport.order !== undefined && sport.order !== state.sports[sport.id].order) {
            const events = Object.values(state.events).filter((event) => event.sportId === sport.id);
            const eventsWithNewOrder = events.map((event) => {
                return Object.assign(new OfferEvent(), {
                    ...event,
                    sportOrder: sport.order,
                });
            });
            dispatch('setEvents', {
                events: eventsWithNewOrder,
            });
        }
        commit(types.SET_SPORTS, {
            ...state.sports,
            [sport.id]: sport,
        });
        StructManager.getInstance().setSports(state.sports);
        dispatch('denormalizePendingEvents');
    }),

    updateTournaments: StoreUtil.asyncErrorGuard(({ commit, dispatch, state }: { commit: any; dispatch: Dispatch; state: ISportOfferState }, tournament: Tournament) => {
        // We might be seeing a new tournament for the first time, hence we need a nullish chain when
        // accessing state tournaments (see Sentry errors).
        if (
            (tournament.order !== undefined && tournament.order !== state.tournaments[tournament.id]?.order)
            || (!!tournament.orderPromoted && tournament.orderPromoted !== state.tournaments[tournament.id]?.orderPromoted)
        ) {
            const events = Object.values(state.events).filter((event) => event.tournamentId === tournament.id);
            const eventsWithNewOrder = events.map((event) => {
                return Object.assign(new OfferEvent(), {
                    ...event,
                    tournamentOrder: tournament.order,
                    specialTournamentOrder: tournament.orderPromoted,
                });
            });
            dispatch('setEvents', {
                events: eventsWithNewOrder,
            });
        }
        commit(types.SET_TOURNAMENTS, {
            ...state.tournaments,
            [tournament.id]: tournament,
        });
        StructManager.getInstance().setTournaments(state.tournaments);
        dispatch('denormalizePendingEvents');
    }),

    updateCategories: StoreUtil.asyncErrorGuard(({ commit, dispatch, state }: { commit: any; dispatch: Dispatch; state: ISportOfferState }, category: Category) => {
        if (category.order !== undefined && category.order !== state.categories[category.id].order) {
            const events = Object.values(state.events).filter((event) => event.categoryId === category.id);
            const eventsWithNewOrder = events.map((event) => {
                return Object.assign(new OfferEvent(), {
                    ...event,
                    categoryOrder: category.order,
                });
            });
            dispatch('setEvents', {
                events: eventsWithNewOrder,
            });
        }
        commit(types.SET_CATEGORIES, {
            ...state.categories,
            [category.id]: category,
        });
        StructManager.getInstance().setCategories(state.categories);
        dispatch('denormalizePendingEvents');
    }),

    updateMarkets: StoreUtil.asyncErrorGuard(({ commit, dispatch, state }: { commit: any; dispatch: Dispatch; state: ISportOfferState }, market: Market) => {
        commit(types.SET_MARKETS, {
            ...state.markets,
            [market.id]: market,
        });
        StructManager.getInstance().setMarkets(state.markets);
        dispatch('denormalizePendingEvents');
    }),

    updateOddTypes: StoreUtil.asyncErrorGuard(({ commit, dispatch, state }: { commit: any; dispatch: Dispatch; state: ISportOfferState }, oddType: OddType) => {
        commit(types.SET_ODD_TYPES, {
            ...state.oddTypes,
            [oddType.id]: oddType,
        });
        StructManager.getInstance().setOddTypes(state.oddTypes);
        dispatch('denormalizePendingEvents');
    }),

    updateMarketGroupInfos: StoreUtil.asyncErrorGuard(({ commit, dispatch, state }: { commit: any; dispatch: Dispatch; state: ISportOfferState }, marketGroupInfo: MarketGroupInfo) => {
        commit(types.SET_MARKET_GROUP_INFOS, {
            ...state.marketGroupInfos,
            [marketGroupInfo.id]: marketGroupInfo,
        });
        StructManager.getInstance().setMarketGroupInfos(state.marketGroupInfos);
        dispatch('denormalizePendingEvents');
    }),

    fetchAllOffer({ dispatch, getters }: { dispatch: Dispatch; getters: any }): void {
        if (getters.isOfferReady) {
            dispatch('fetchAll');
            dispatch('fetchTopEvents');
            dispatch('fetchOutrights');
            dispatch('fetchSuperEvents');
        }
    },

    startLiveEventsCounting: ({ commit }: { commit: Commit }): void => {
        OfferService.getInstance().subscribeToLiveEventsCountChangesStream(
            (eventsCount: number) => {
                errorGuard(() => commit(types.SET_LIVE_EVENTS_COUNT, eventsCount));
            },
        );
    },

    startLiveEventsCountingOnly: ({ commit }: { commit: Commit }): void => {
        OfferService.getInstance().subscribeToLiveEventsCountChangesStreamOnly(
            (eventsCount: number) => {
                errorGuard(() => commit(types.SET_LIVE_EVENTS_COUNT, eventsCount));
            },
        );
    },

    _purgeOddValueDynamics(
        { getters, commit }: { getters: any; commit: Commit },
    ): void {
        const oddChanges: Record<number, Record<string, OddDynamics>> = {};

        const now = new Date();
        _forEach(getters.oddValueDynamics, (dynamicsItems: Record<string, OddDynamics>, eventId: string) => {
            const eventIdInt = parseInt(eventId, 10);
            _forEach(dynamicsItems, (oddDynamics: OddDynamics, oddId: string) => {
                const preserveTimeSeconds = 3;
                if (differenceInSeconds(now, oddDynamics.created) < preserveTimeSeconds) {
                    if (!oddChanges[eventIdInt]) {
                        oddChanges[eventIdInt] = {};
                    }
                    oddChanges[eventIdInt][oddId] = oddDynamics;
                }
            });
        });

        commit(types.SET_ODD_VALUE_DYNAMICS, oddChanges);
    },

    _deleteFromOddValueDynamics(
        { getters, commit }: { getters: any; commit: Commit },
        eventId: number,
    ): void {
        const oddChanges = { ...getters.oddValueDynamics };
        delete oddChanges[eventId];
        commit(types.SET_ODD_VALUE_DYNAMICS, oddChanges);
    },

    _setOddValueDynamics(
        { getters, commit }: { getters: any; commit: Commit },
        events: OfferEvent[],
    ): void {
        const oddChanges = { ...getters.oddValueDynamics };
        const now = new Date();
        // eslint-disable-next-line sonarjs/cognitive-complexity
        events.forEach((event) => {
            if (getters.eventsMap[event.id]) {
                const oldOdds: Record<string, number> = {};

                getters.eventsMap[event.id].getOdds().forEach((odd: Odd) => {
                    oldOdds[odd.uniqueId] = odd.value;
                });

                if (!oddChanges[event.id]) {
                    oddChanges[event.id] = {};
                }

                event.getOdds().forEach((odd) => {
                    const oldOddValue: number = oldOdds[odd.uniqueId];
                    if (oldOddValue && oldOddValue.toFixed(2) !== odd.value.toFixed(2)) {
                        let newDirection = odd.value < oldOddValue ? OddValueChange.down1 : OddValueChange.up1;
                        const oldChange = oddChanges[event.id][odd.uniqueId];

                        if (oldChange) {
                            if (newDirection < 0 && oldChange.oddDirection < 0) {
                                newDirection = (oldChange.oddDirection) % 2 - 1;
                            } else if (newDirection > 0 && oldChange.oddDirection > 0) {
                                newDirection = (oldChange.oddDirection) % 2 + 1;
                            }
                        }

                        oddChanges[event.id][odd.uniqueId] = {
                            oddDirection: newDirection,
                            created: now,
                        };
                    }
                });

                if (_isEmpty(oddChanges[event.id])) {
                    delete oddChanges[event.id];
                }
            }
        });

        commit(types.SET_ODD_VALUE_DYNAMICS, oddChanges);
    },

    fetchMarketGroups: asyncErrorGuard(async (
        { commit, state }: { commit: Commit, state: ISportOfferState },
        { sportId, marketGroupPhase }: { sportId: number, marketGroupPhase: MarketGroupPhase },
    ) => {
        const marketGroupInfos = await OfferService.getInstance().getMarketGroups(sportId, marketGroupPhase);
        commit(types.SET_MARKET_GROUP_INFOS, {
            ...state.marketGroupInfos,
            ...keyBy(marketGroupInfos, 'id'),
        });
    }),

    fetchSportDatesWithActiveOffer: asyncErrorGuard(async (
        { commit }: { commit: Commit },
        sportId: number,
    ) => {
        const dates = await OfferService.getInstance().getDatesWithActiveOffer(sportId);
        commit(types.SET_SPORT_DATES_WITH_ACTIVE_OFFER, { dates, sportId });
    }),

    fetchSportsWithEvents: asyncErrorGuard(async (
        { commit }: { commit: Commit },
    ) => {
        const sportIds = await OfferService.getInstance().fetchSportsWithEvents();
        commit(types.SET_SPORTS_WITH_EVENTS, sportIds);
    }),

    fetchPopularSuperbets: asyncErrorGuard(async (
        { commit, dispatch }: { commit: Commit, dispatch: Dispatch },
    ) => {
        const popularSuperbets = await getPopularSuperbets();
        const eventIds = [...new Set(popularSuperbets.map((superbet) => superbet.eventID))];
        const events = await OfferService.getInstance().getEventsById(eventIds);

        await dispatch(
            'setEvents',
            {
                events,
            },
        );
        commit(types.SET_POPULAR_SUPERBETS, popularSuperbets);
    }),

    fetchPopularSuperbetsV2: asyncErrorGuard(async (
        { commit }: { commit: Commit },
    ) => {
        const popularSuperbets = await getPopularSuperbetsV2();

        commit(types.SET_POPULAR_SUPERBETS_V2, popularSuperbets);
    }),
};
